import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import ConsultantsTabBlock from './ConsultantsTabBlock';
import CustomCircularProgress from '../Custom Components/CustomCircularProgress/CustomCircularProgress';
import EditIcon from '@mui/icons-material/Edit';
import { useState, useRef, useEffect } from 'react';

// Renders a full HTML document string in a sandboxed, auto-resizing iframe
const IframeContent = ({ html }) => {
  const iframeRef = useRef(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const resizeIframe = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
          const height = doc.documentElement.scrollHeight || doc.body.scrollHeight;
          iframe.style.height = height + 'px';
        }
      } catch (e) {
        // cross-origin guard (shouldn't happen with srcdoc)
      }
    };

    iframe.addEventListener('load', resizeIframe);
    return () => iframe.removeEventListener('load', resizeIframe);
  }, [html]);

  return (
    <iframe
      ref={iframeRef}
      srcDoc={html}
      style={{
        width: '100%',
        minHeight: '400px',
        border: 'none',
        display: 'block',
      }}
      sandbox="allow-same-origin"
      title="AI Generated Document"
    />
  );
};

const ConsultantsTabContent = ({
  tabContent,
  submitAnswer = () => {},
  tabDataHandler = () => {},
  aiSuggestionData = null, 
  aiSuggestionHandler = null,
  asdFetcher = () => {},
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFetching, setIsFetching] = useState(false);

  const getAISuggestions = async () => {
    if (!aiSuggestionHandler) return;
    try {
      setIsGenerating(true);
      const response = await aiSuggestionHandler();
      if (response) {
        setIsGenerating(false);
        setIsFetching(true);
        await asdFetcher();
        setIsFetching(false);
      } else {
        setIsFetching(false);
        setIsGenerating(false);
      }
    } catch (e) {
      setIsFetching(false);
      setIsGenerating(false);
    }
  };

  return (
    <Box mt={3} ml={2} sx={{ borderBottom: 1, borderColor: 'divider' }} pb={3}>
      <Box display={'flex'} alignItems={'center'}>
        <Typography
          component={'h3'}
          variant="dashboardCompHeading"
          className="fontSize26"
          mr={2}
        >
          {tabContent?.tabName}
        </Typography>
        {tabContent?.completionPercentage !== undefined ? (
          <CustomCircularProgress
            value={tabContent?.completionPercentage || 0}
          />
        ) : (
          ''
        )}
      </Box>
      <ConsultantsTabBlock
        handleAnswerSubmit={submitAnswer}
        blockDetail={[...(tabContent?.questions || [])].sort(
          (a, b) => a?.sortby - b?.sortby
        )}
        dataHandler={tabDataHandler}
      />
      {aiSuggestionHandler && (
        <Box className="displayFlex justifyCenter">
          <IconButton
            onClick={getAISuggestions}
            className={
              tabContent?.completionPercentage === 100
                ? 'iconButtonType6 mr0'
                : 'iconButtonType7 mr0'
            }
            disabled={
              (tabContent?.completionPercentage === 100 ? false : true) ||
              isFetching ||
              isGenerating
            }
          >
            {isGenerating || isFetching ? (
              <CircularProgress className="mr05" size={'1rem'} />
            ) : (
              <EditIcon fontSize="small" className="mr05" />
            )}
            {isGenerating
              ? 'Generating AI Document'
              : isFetching
              ? 'Fetching Document'
              : 'Generate AI Document'}
          </IconButton>
        </Box>
      )}
      {aiSuggestionData && (
        <Box mt={3}>
          <Typography
            component="h4"
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'rgb(20, 0, 110)',
              mb: 1,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            AI Generated Document
          </Typography>
          <Box
            sx={{
              border: '1px solid rgba(20, 0, 110, 0.15)',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <IframeContent html={aiSuggestionData} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ConsultantsTabContent;
